import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { UserPlus, Trash, ShieldCheck, Crown, UserCircle, Lock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import SearchSortBar from "@/components/SearchSortBar";
import useSearchSort from "@/hooks/useSearchSort";
import { useAuth } from "@/contexts/AuthContext";

const PROTECTED_ROLES = new Set(["global_admin", "clariva_support"]);

export default function Team() {
  const { user: currentUser } = useAuth();
  const [team, setTeam] = useState({ members: [], invites: [] });
  const [roles, setRoles] = useState([]);
  const [openInvite, setOpenInvite] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("reviewer");

  const load = useCallback(() => api.get("/team").then(({ data }) => setTeam(data)), []);
  useEffect(() => {
    load();
    api.get("/team/roles").then(({ data }) => setRoles(data.predefined));
  }, [load]);

  const invite = async () => {
    if (!email) { toast.error("Email required"); return; }
    try {
      await api.post("/team/invite", { email, role });
      toast.success(`Invite sent to ${email}`);
      setOpenInvite(false); setEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
  };

  const revoke = async (inviteId) => {
    await api.delete(`/team/invites/${inviteId}`);
    toast.success("Invite revoked");
    load();
  };

  const updateRole = async (memberId, newRole) => {
    try {
      await api.patch(`/team/${memberId}`, { role: newRole });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const roleIcon = (r) => r === "admin" ? <Crown size={14} weight="fill" className="text-amber-400" /> : r === "manager" ? <ShieldCheck size={14} weight="duotone" /> : <UserCircle size={14} />;

  return (
    <PageShell
      title="Team & Roles"
      subtitle="Invite members and manage role-based access"
      right={
        <Dialog open={openInvite} onOpenChange={setOpenInvite}>
          <DialogTrigger asChild>
            <Button data-testid="invite-member-btn" className="gap-2"><UserPlus size={14} /> Invite member</Button>
          </DialogTrigger>
          <DialogContent className="glass-strong border-0 max-w-md">
            <DialogHeader><DialogTitle>Invite a team member</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <div className="overline mb-1">Email</div>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@company.com" data-testid="invite-email-input" className="glass-subtle border-[var(--border)]" />
              </div>
              <div>
                <div className="overline mb-1">Role</div>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="invite-role-select" className="glass-subtle border-[var(--border)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-xs text-[var(--text-3)] mt-1">{roles.find((r) => r.id === role)?.description}</div>
              </div>
              <Button onClick={invite} data-testid="send-invite-btn" className="w-full">Send invite</Button>
            </div>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {roles.map((r, i) => (
          <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <GlassCard className="p-5 h-full">
              <div className="flex items-center gap-2 mb-2">
                {roleIcon(r.id)}
                <h3 className="text-base font-medium" style={{ fontFamily: 'var(--font-heading)' }}>{r.name}</h3>
              </div>
              <div className="text-xs text-[var(--text-2)] leading-relaxed">{r.description}</div>
              <div className="mt-3 flex flex-wrap gap-1">
                {r.permissions.slice(0, 4).map((p) => (
                  <span key={p} className="chip chip-neutral text-[9px]">{p}</span>
                ))}
                {r.permissions.length > 4 && <span className="chip chip-neutral text-[9px]">+{r.permissions.length - 4}</span>}
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>

      <GlassCard className="p-0 overflow-hidden mb-6">
        <div className="p-4 border-b border-[var(--border)] overline">Members ({team.members.length})</div>
        <div className="p-4 pb-0"><TeamMembersTable members={team.members} roles={roles} updateRole={updateRole} currentUser={currentUser} /></div>
      </GlassCard>

      {team.invites.length > 0 && (
        <GlassCard className="p-0 overflow-hidden">
          <div className="p-4 border-b border-[var(--border)] overline">Pending invites ({team.invites.length})</div>
          <table className="w-full text-sm">
            <tbody>
              {team.invites.map((inv) => (
                <tr key={inv.id} className="border-b border-[var(--border)]">
                  <td className="p-4 font-medium">{inv.email}</td>
                  <td className="p-4 text-[var(--text-2)]">{inv.role}</td>
                  <td className="p-4 text-xs text-[var(--text-3)]">{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td className="p-4 text-end">
                    <Button variant="ghost" size="sm" onClick={() => revoke(inv.id)} data-testid={`revoke-invite-${inv.id}`} className="text-[var(--danger)] gap-1"><Trash size={12} /> Revoke</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassCard>
      )}
    </PageShell>
  );
}

function TeamMembersTable({ members, roles, updateRole, currentUser }) {
  const s = useSearchSort(members, {
    searchKeys: ["full_name", "email", "role"],
    sortOptions: [
      { value: "full_name", label: "Name A→Z", defaultDir: "asc" },
      { value: "email", label: "Email", defaultDir: "asc" },
      { value: "role", label: "Role", defaultDir: "asc" },
      { value: "is_active", label: "Status", defaultDir: "desc" },
    ],
    defaultSort: "full_name",
  });
  return (
    <div>
      <SearchSortBar controller={s} placeholder="Search members by name, email, or role…" testIdPrefix="team" />
      <div className="-mx-4">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-2)]">
            <tr>
              <th className="text-start p-4 overline">Name</th>
              <th className="text-start p-4 overline">Email</th>
              <th className="text-start p-4 overline">Role</th>
              <th className="text-start p-4 overline">Status</th>
            </tr>
          </thead>
          <tbody>
            {s.items.map((m) => {
              const isProtected = !!m.is_global || PROTECTED_ROLES.has(m.role);
              const isSelf = currentUser && m.id === currentUser.id;
              const locked = isProtected || isSelf;
              const lockReason = isProtected
                ? "Global Admin / Clariva Support accounts are protected."
                : isSelf
                ? "You can't change your own role — ask another admin."
                : "";
              return (
              <tr key={m.id} className="border-b border-[var(--border)]" data-testid={`member-row-${m.id}`}>
                <td className="p-4 font-medium flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[var(--surface-strong)] border border-[var(--border-strong)] flex items-center justify-center text-xs font-semibold">{m.full_name?.charAt(0)}</div>
                  <div className="flex items-center gap-1.5">
                    {m.full_name}
                    {isProtected && <span title="Protected account" data-testid={`member-protected-${m.id}`}><Lock size={12} className="text-[var(--text-3)]" /></span>}
                  </div>
                </td>
                <td className="p-4 text-[var(--text-2)]">{m.email}</td>
                <td className="p-4">
                  {locked ? (
                    <div
                      className="w-40 h-8 inline-flex items-center gap-1.5 px-3 rounded-md border border-dashed border-[var(--border)] text-xs text-[var(--text-2)] cursor-not-allowed"
                      title={lockReason}
                      data-testid={`member-role-locked-${m.id}`}
                    >
                      <Lock size={11} /> {roles.find((r) => r.id === m.role)?.name || m.role}
                    </div>
                  ) : (
                    <Select value={m.role} onValueChange={(v) => updateRole(m.id, v)}>
                      <SelectTrigger className="w-40 h-8 glass-subtle border-[var(--border)]" data-testid={`member-role-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{roles.filter((r) => !PROTECTED_ROLES.has(r.id)).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </td>
                <td className="p-4"><span className={`chip ${m.is_active ? "chip-low" : "chip-neutral"}`}>{m.is_active ? "active" : "inactive"}</span></td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {s.items.length === 0 && <div className="text-center text-xs text-[var(--text-3)] py-8">{members.length ? "No matches." : "No members yet."}</div>}
    </div>
  );
}
